import classNames from 'classnames';
import styles from './styles.module.scss';
import { Icons } from 'pages/Profile/components/icons';
import { useEffect, useState } from 'react';
import { Modal } from 'components-antd';
import { CloseModalIconSmall } from 'pages/ServicesCategory/components/AddVendorModal/icons';
import { Button, Spinner } from 'components';
import { getClientTagsForAgentByTeam } from 'api/agentTeamDetail';
import { showErrorMessage, showSuccessMessage } from 'helpers';
import ClientTagsInput from 'components/ClientsDrawer/InviteClient/StageParts/ClientDetails/ClientTags';
import { v4 as uuid } from 'uuid';
import { putUserData } from 'api/user';

type ClientTagsPropsType = {
  clientId: number;
  assignedTags: any[];
};

const ClientTags = (props: ClientTagsPropsType) => {
  const { assignedTags, clientId } = props;
  const maxDisplaySize = 4;
  const [showControls, setShowControls] = useState<boolean>(false);
  const [showAll, setShowAll] = useState<boolean>(false);
  const [displayTags, setDisplayTags] = useState<any[]>([]);
  const [internalTags, setInternalTags] = useState<any[]>(assignedTags);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [clientTags, setClientTags] = useState<any[]>([]);
  const [temporaryTag, setTemporaryTag] = useState<any>(undefined);
  const [payloadTags, setPayloadTags] = useState<{ name: string; value?: number; uuid: string }[]>(
    [],
  );

  useEffect(() => {
    fetchClientTags();
  }, []);

  useEffect(() => {
    if (internalTags?.length <= maxDisplaySize) setShowControls(false);
    else setShowControls(true);
  }, [internalTags]);

  useEffect(() => {
    if (!showModal) setPayloadTags([]);
  }, [showModal]);

  useEffect(() => {
    if (!internalTags?.length) return;
    if (internalTags.length <= maxDisplaySize || showAll) setDisplayTags(internalTags);
    else {
      const first4 = internalTags.slice(0, 4);
      setDisplayTags(first4);
    }
  }, [internalTags, showAll]);

  useEffect(() => {
    if (!internalTags?.length || !showModal) return;

    const tagsWithUuids = internalTags.map((tag) => ({
      name: tag.TagName,
      value: tag.Id,
      uuid: uuid(),
    }));
    setPayloadTags(tagsWithUuids);
  }, [internalTags, showModal]);

  const fetchClientTags = async () => {
    setLoading(true);
    try {
      const response = await getClientTagsForAgentByTeam();
      if (response.status === 200 && response.data?.result?.length)
        setClientTags(response.data.result);
    } catch (e) {
      showErrorMessage('Unable to fetch client tags.');
    }
    setLoading(false);
  };

  const handleTagsAssignment = async () => {
    if (saving) return;

    setSaving(true);
    try {
      const preparedTagsArray = payloadTags.length
        ? payloadTags.map((tag) => ({
            Id: tag.value,
            TagName: tag.name,
          }))
        : [];

      if (temporaryTag)
        preparedTagsArray.push({ Id: temporaryTag.value, TagName: temporaryTag.name });

      const sendData = {
        Values: {
          ClientTags: preparedTagsArray,
        },
      };

      const response = await putUserData(sendData, { userId: clientId, urlRole: 'client' });

      if (response.status === 200) {
        setInternalTags(response?.data?.result?.Client?.ClientTags ?? []);
        showSuccessMessage('Client tags updated.');
        setShowModal(false);
      }
    } catch (e) {
      showErrorMessage('Unable to update client tags.');
    }
    setSaving(false);
  };

  return (
    <div className={styles.clientTags}>
      {internalTags?.length ? (
        <>
          <div className={styles.heading}>Tag(s)</div>
          <div className={styles.content}>
            {displayTags.map((tag) => (
              <div className={styles.tag}>{tag.TagName}</div>
            ))}
            {showControls && !showAll ? (
              <div
                className={classNames(styles.tag, styles.more)}
                onClick={() => setShowAll(true)}
              >{`+${internalTags.length - maxDisplaySize} more`}</div>
            ) : null}
            <div className={classNames(styles.tag, styles.add)} onClick={() => setShowModal(true)}>
              <Icons variant={Icons.ADD} />
              <div>Add</div>
            </div>
          </div>
          {showControls && showAll ? (
            <div className={styles.less} onClick={() => setShowAll(false)}>
              <div className={styles.text}>Show Less</div>
            </div>
          ) : null}
        </>
      ) : (
        <div className={styles.addBox} onClick={() => setShowModal(true)}>
          <Icons variant={Icons.ADD} />
          <div>Add Tags</div>
        </div>
      )}
      {showModal && (
        <Modal
          className={styles.tagsModal}
          footer={null}
          width={675}
          open={showModal}
          onCancel={() => setShowModal(false)}
          destroyOnClose
          closable={true}
          zIndex={1000}
        >
          {loading ? (
            <Spinner />
          ) : (
            <div className={styles.modalContent}>
              <div className={styles.header}>
                <div></div>
                <div>Add Tags</div>
                <div className={styles.close} onClick={() => setShowModal(false)}>
                  <CloseModalIconSmall />
                </div>
              </div>
              <div className={styles.content}>
                <ClientTagsInput
                  clientTags={clientTags}
                  assignedTags={payloadTags}
                  setAssignedTags={setPayloadTags}
                  temporaryTag={temporaryTag}
                  setTemporaryTag={setTemporaryTag}
                />
              </div>
              <div className={styles.footer}>
                <Button
                  type="submit"
                  className={styles.saveButton}
                  title={'Save'}
                  onClick={handleTagsAssignment}
                  disabled={saving || (!payloadTags.length && !temporaryTag)}
                  isPending={saving}
                />
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

export default ClientTags;
